import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import ArrowScroll from 'components/ArrowScroll';
import CashOutButton from 'components/CashOutButton';
import FavoriteStar from 'components/FavoriteStar';
import MarketCollapseRow from 'components/MarketCollapseRow';
import MarketLinesCarousel from 'components/MarketLinesCarousel/MarketLinesCarousel';
import MarketBackLayRow from 'components/MarketSport/components/MarketBackLayRow';
import MarketLineRangeInfoRow from 'components/MarketSport/components/MarketLineRangeInfoRow';
import MobileSelectionActionsAndPLTable from 'components/MarketSport/components/MobileSelectionActionsAndPLTable/MobileSelectionActionsAndPLTable';
import StatusOverlay from 'components/MarketsTable/components/MarketsTableRow/components/StatusOverlay';
import { CookieNames, FAVORITES_TYPES, PageBlocks, PlacementPages, tooltipStatus } from 'constants/app';
import { PLTableType } from 'constants/marketsTable';
import useMarketStatusAndLockIcon from 'hooks/useMarketStatusAndLockIcon';
import useMarketWithManySelections from 'hooks/useMarketWithManySelections';
import { getFavoriteById } from 'redux/modules/favorites/selectors';
import { TFavorite } from 'redux/modules/favorites/type';
import { getSingleMarketInfo } from 'redux/modules/market/selectors';
import {
  getIsMarketInPlayByMarketPricesId,
  getMarketPricesId,
  getMarketPricesIsBettingEnabled,
  getStatusByMarketPricesId
} from 'redux/modules/marketsPrices/selectors';
import { resetMultiMarketArrowScrollParams, setMultiMarketShowAll } from 'redux/modules/multiMarket';
import { getMultiMarketShowAll } from 'redux/modules/multiMarket/selectors';
import { getMarketTypes } from 'utils/market';

import multiMarketStyles from '../../styles.module.scss';
import styles from './styles.module.scss';

interface MobileExtraTabMultiMarketProps {
  /**
   * Whether market depth is enabled
   */
  isDepthEnabled: boolean;
  /**
   * Whether is landscape mode
   */
  isLandscape: boolean;
}

const MobileExtraTabMultiMarket = ({ isDepthEnabled, isLandscape }: MobileExtraTabMultiMarketProps) => {
  const dispatch = useDispatch();

  const market = useSelector(getSingleMarketInfo);
  const isFavorite = useSelector(getFavoriteById(market?.marketId));
  const marketPricesId = useSelector(getMarketPricesId(market?.marketId ?? ''));
  const bettingEnabled = useSelector(getMarketPricesIsBettingEnabled(market?.marketId ?? ''));
  const marketPricesStatus = useSelector(getStatusByMarketPricesId(market?.marketId ?? ''));
  const isInPlay = useSelector(getIsMarketInPlayByMarketPricesId(market?.marketId ?? ''));
  const showAll = useSelector(getMultiMarketShowAll);
  const [cookies, setCookie] = useCookies([CookieNames.PL_TABLE_VISIBLE]);

  const favoriteData = {
    starred: !isFavorite,
    entryId: market?.marketId,
    entryName: market?.marketName,
    entryType: FAVORITES_TYPES.market,
    sportId: market?.eventType.id
  };

  const { status, showStatus, isOpeningSoonStatus } = useMarketStatusAndLockIcon(bettingEnabled, marketPricesStatus);
  const {
    isHandicapDoubleLine,
    currentSelectionsLine,
    defaultLineNumber,
    keyLineNumber,
    isSelectionUpdated,
    setCurrentSelectionsLine
  } = useMarketWithManySelections({ market: market! });

  const isShowPlTable = !!market && PLTableType.includes(market.description?.bettingType);

  const [collapsed, setCollapsed] = useState<boolean>(false);

  let isLineMarket = false;

  if (market) {
    const marketTypes = getMarketTypes(market.description.marketType, market.description.bettingType);
    isLineMarket = marketTypes.isLineMarket;
  }

  useEffect(() => {
    dispatch(resetMultiMarketArrowScrollParams());

    return () => {
      dispatch(resetMultiMarketArrowScrollParams());
    };
  }, []);

  const handleShowAllRunners = useCallback(() => {
    dispatch(setMultiMarketShowAll(true));

    if (currentSelectionsLine !== keyLineNumber) {
      setCurrentSelectionsLine(keyLineNumber);
    }
  }, [keyLineNumber, currentSelectionsLine, setCurrentSelectionsLine, dispatch]);

  const handleMinimize = useCallback(() => {
    dispatch(setMultiMarketShowAll(false));

    if (currentSelectionsLine !== keyLineNumber) {
      setCurrentSelectionsLine(keyLineNumber);
    }
  }, [keyLineNumber, currentSelectionsLine, setCurrentSelectionsLine, dispatch]);

  const collapseMarket = () => {
    setCollapsed(prevState => !prevState);
  };

  const handlerPLTable = () => {
    setCookie(CookieNames.PL_TABLE_VISIBLE, cookies.PL_TABLE_VISIBLE ? '' : true);
  };

  const handleToggleShowAll = () => {
    dispatch(setMultiMarketShowAll(!showAll));
  };

  if (!marketPricesId || !market?.marketId) {
    return null;
  }

  return (
    <>
      <MarketCollapseRow
        onMarketCollapse={collapseMarket}
        inPlay={market.inPlay || isInPlay}
        marketId={market.marketId}
        marketStartTime={market.marketStartTime}
        marketName={market.marketName}
        sportId={market.eventType.id}
        className="biab_multi-market-title"
        classes={{
          header: classNames(
            multiMarketStyles.multiMarket__otherMarkets__collapseHeader,
            multiMarketStyles.multiMarket__otherMarkets__collapseHeader__mobile
          )
        }}
        rightIcon={
          <i
            className={classNames('biab_collapse-toggler fa2', {
              'fa2-arrow-up': !collapsed,
              'fa2-arrow-down': collapsed
            })}
          />
        }
        inPlayClassName="biab_multi-market-inplay"
        isMarketCashOutEnabled={market.cashOutEnabled}
        inPlayStatusFirst
      >
        {market && <FavoriteStar entryData={favoriteData as TFavorite} className={styles.eventMarket__starIcon} />}
        <span className={styles.marketName} data-market-name={market.marketName}>
          {market.marketName}
        </span>
      </MarketCollapseRow>
      {!collapsed && (
        <>
          {isLineMarket && <MarketLineRangeInfoRow market={market} />}
          <div className="biab_handicap-market">
            {market.cashOutEnabled && !tooltipStatus[status || ''] && (
              <CashOutButton marketId={market.marketId} partialCashOutEnabled={market.partialCashOutEnabled} />
            )}
            <MobileSelectionActionsAndPLTable
              market={market}
              onShowAllRunners={handleShowAllRunners}
              showAllRunners={showAll}
              onMinimize={handleMinimize}
              isShowPlTable={isShowPlTable}
              onTogglePLTable={handlerPLTable}
              isOpeningSoonStatus={isOpeningSoonStatus}
            />
            {isHandicapDoubleLine && !showAll && (
              <MarketLinesCarousel
                market={market}
                onToggleShowLines={handleToggleShowAll}
                showAll={showAll}
                currentSelectionsLine={currentSelectionsLine}
                setCurrentSelectionsLine={setCurrentSelectionsLine}
                isOpeningSoonStatus={isOpeningSoonStatus}
                defaultLineNumber={defaultLineNumber}
                shouldScrollOnCurrentLineUpdate={!isSelectionUpdated}
              />
            )}
            <MarketBackLayRow
              market={market}
              isDepthEnabled={isDepthEnabled}
              showDeepPrices
              page={PlacementPages.MULTI_MARKET}
              wrapperClassName={styles.mobileBackLayRow}
            />
            <div className={styles.mobileExtraTabSingleMarket__runnersContainer}>
              <ArrowScroll
                market={market}
                showAll={showAll}
                pageBlock={PageBlocks.MULTI_MARKET_VIEW}
                isDepthEnabled={isDepthEnabled}
                isLandscape={isLandscape}
                page={PlacementPages.MULTI_MARKET}
                currentSelectionsLine={currentSelectionsLine}
                keyLineNumber={defaultLineNumber}
              />
              {showStatus && <StatusOverlay status={status} inMiddle />}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MobileExtraTabMultiMarket;
